import React, { FC, useContext } from 'react'
import ChartJS from '../../../components/common/charts/ChartJS'
import { brandColors } from 'shared/basicPalette'
import {
  useMediaDetailEngagementChartQuery,
  useMediaDetailVideoDurationQuery
} from './MediaDetailAnalytics.codegen'
import Loader from '../../../components/common/loader/Loader'
import dayjs from 'dayjs'
import { groupBy } from 'lodash'
import { mergeIntervals } from 'shared/videoImpression'
import { t, Trans } from '@lingui/macro'
import { Center } from 'front-end/src/components'
import { AnalyticsContext } from '../AnalyticsContext'

const TOTAL_CHART_WIDTH = 710
const POINT_PER_PX = 4
const MAX_CHART_POINTS = Math.floor(TOTAL_CHART_WIDTH / POINT_PER_PX)

export const MediaEngagementChart: FC<{
  topicElementId: number
}> = ({ topicElementId }) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const { data: videoLengthData, loading: videoLengthLoading } =
    useMediaDetailVideoDurationQuery({
      fetchPolicy: 'no-cache',
      variables: {
        mainAnalyticsFilter: filterAsQueryVariables,
        topicElementId
      }
    })

  const { data, loading } = useMediaDetailEngagementChartQuery({
    fetchPolicy: 'no-cache',
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      topicElementId
    }
  })

  if (loading || videoLengthLoading) {
    return (
      <Center w="100%">
        <Loader />
      </Center>
    )
  }

  const firstImpression =
    videoLengthData?.analytics.media.impressionsOverview?.items?.[0]

  if (!firstImpression) {
    return (
      <Center mt="24px" color={brandColors.grey.mid}>
        <Trans>No engagement data</Trans>
      </Center>
    )
  }

  const videoDuration = (firstImpression?.video_duration_ms || 0) + 1000 // extend chart by 1 second to see better the end of engagements.
  const videoDurationInSec = Math.ceil(videoDuration / 1000)

  const totalPoints =
    videoDurationInSec > MAX_CHART_POINTS
      ? MAX_CHART_POINTS
      : videoDurationInSec

  const pointRange = videoDuration / totalPoints
  const uniqueEngValues = Array(totalPoints).fill(0)
  const totalEngValues = Array(totalPoints).fill(0)

  const impressions = data?.analytics.media.impressionsOverview?.items
  const uniqueImpressionsByUser = groupBy(
    data?.analytics.media.impressionsOverview?.items,
    'userId'
  )

  const timePoints = Array(totalPoints)
    .fill(0)
    .map((_, i) => {
      // Unique engagements
      const currentTimeMs = Math.floor(pointRange * i)
      for (const [, userImpressions] of Object.entries(
        uniqueImpressionsByUser
      )) {
        const groupedEngagements = userImpressions.reduce(
          (acc: any, currval: any) => {
            //TODO: types
            return [
              ...acc,
              ...currval.engagements.map((e) => {
                return [e.from, e.to]
              })
            ]
          },
          []
        )

        mergeIntervals(groupedEngagements).map((e) => {
          if (currentTimeMs >= e[0] && currentTimeMs <= e[1]) {
            uniqueEngValues[i] = uniqueEngValues[i] + 1
          } else {
            uniqueEngValues[i] = uniqueEngValues[i]
          }
        })
      }

      // Repeated engagements
      impressions?.map((impression) => {
        impression.engagements.map((engagement) => {
          if (
            currentTimeMs >= engagement.from &&
            currentTimeMs <= engagement.to
          ) {
            totalEngValues[i] = totalEngValues[i] + 1
          } else {
            totalEngValues[i] = totalEngValues[i]
          }
        })
      })

      return dayjs.duration(currentTimeMs).format('m:ss')
    })

  return (
    <ChartJS
      width={TOTAL_CHART_WIDTH}
      height={190}
      enhanceMaxValue={false}
      config={{
        type: 'line',
        data: {
          labels: timePoints,
          datasets: [
            {
              label: t`Unique engagements`,
              data: uniqueEngValues,
              backgroundColor: brandColors.blue.light,
              pointRadius: 0,
              borderWidth: 0,
              cubicInterpolationMode: 'monotone'
            },
            {
              label: t`Engagements`,
              data: totalEngValues,
              backgroundColor: brandColors.yellow.light,
              pointRadius: 0,
              borderWidth: 0,
              cubicInterpolationMode: 'monotone'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                },
                ticks: {
                  fontColor: '#b1bfc5',
                  maxRotation: 0,
                  maxTicksLimit: 10
                }
              }
            ],
            yAxes: [
              {
                gridLines: {
                  drawBorder: false,
                  borderDash: [8, 4]
                },
                ticks: {
                  fontColor: '#b1bfc5',
                  beginAtZero: true,
                  min: 0,
                  callback: function (value: number) {
                    return `${(
                      (100 * value) /
                      Math.max(...uniqueEngValues)
                    ).toFixed(0)}%`
                  }
                }
              }
            ]
          }
        }
      }}
    />
  )
}
